import { Button } from '@edsn/pie-components';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { CancelCreateTicketDialog } from '#pie/components/cancel-create-ticket-dialog/CancelCreateTicketDialog';
import { InputCheckbox } from '#pie/components/input-checkbox/InputCheckbox';
import { PageHead } from '#pie/components/page-head/PageHead';
import { Stack } from '#pie/components/stack/Stack';
import { ToastButton } from '#pie/components/toast/Toast';
import { useToast } from '#pie/components/toast/ToastContext';
import { VITE_NON_CONFORMITY_CATEGORY_ID } from '#pie/env';
import { useGetMainCategory, usePostTicket } from '#pie/internal/api/pie-bff';
import { useStoredParams } from '#pie/stores/searchParams';
import { Stepper } from '../../../components/stepper/Stepper';
import {
  codesSchema,
  TicketCreateNonConformityCodes,
} from './TicketCreateNonConformityCodes';
import {
  detailsSchema,
  TicketCreateNonConformityDetails,
} from './TicketCreateNonConformityDetails';
import { TicketCreateNonConformityTickets } from './TicketCreateNonConformityTickets';

export const nonConformitySchema = z.object({
  ...codesSchema.shape,
  ...detailsSchema.shape,
  measureField: z.string(),
  observationDate: z.string(),
  placeName: z.string().optional(),
  rejections: z
    .array(
      z.object({
        rejectionCategoryId: z.string(),
        rejectionReasonId: z.string().nullable(),
      }),
    )
    .min(1),
});

export type PartialNonConformitySchema = Partial<
  z.infer<typeof nonConformitySchema>
>;
interface NonConformityFormContext {
  data: PartialNonConformitySchema;
  setStepShouldBlock: (stepShouldBlock: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
}

export const NonConformityFormContext = createContext<
  NonConformityFormContext | undefined
>(undefined);

export const useNonConformityFormContext = () => {
  const context = useContext(NonConformityFormContext);
  if (!context) {
    throw new Error(
      'useEanFormContext must be used within a NonConformityFormContext',
    );
  }
  return context;
};

export interface StepProps {
  wizardActions: ReactNode;
  stepper: ReactNode;
  onSubmit: (data: PartialNonConformitySchema) => void;
  onBack: () => void;
}

export const useNonConformityCategory = () => {
  const { data, isLoading } = useGetMainCategory();

  const mainCategory = data?.items.find(
    (c) => c.id === VITE_NON_CONFORMITY_CATEGORY_ID,
  );
  const subCategories = mainCategory?.subCategories.at(0);

  return {
    isLoading,
    mainCategoryId: mainCategory?.id,
    subCategoryId: subCategories?.id,
  };
};

export const TicketCreateNonConformity = ({
  defaultValues,
}: {
  defaultValues?: PartialNonConformitySchema;
}) => {
  const { t } = useTranslation();
  const { t: transtic } = useTranslation('ticket');
  const params = useStoredParams('/berichtenbox');
  const [step, setStep] = useState(0);
  const [shouldBlock, setShouldBlock] = useState<[boolean, boolean, boolean]>([
    false,
    false,
    false,
  ]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<PartialNonConformitySchema>({
    rejections: [{ rejectionCategoryId: '', rejectionReasonId: null }],
    ...defaultValues,
  });
  const [createAnother, setCreateAnother] = useState<boolean>(false);
  const title = `NC-${data.electricityEan}`;

  const stepComponents = [
    TicketCreateNonConformityCodes,
    TicketCreateNonConformityTickets,
    TicketCreateNonConformityDetails,
  ] as const;
  const Step = stepComponents[step];

  const navigate = useNavigate();
  const { addToast } = useToast();

  const {
    subCategoryId,
    mainCategoryId,
    isLoading: isCategoryLoading,
  } = useNonConformityCategory();

  const {
    mutate,
    isPending: isPostLoading,
    isSuccess,
  } = usePostTicket({
    mutation: {
      onError(error) {
        addToast({
          message: transtic('create.toast.create_error.message'),
          title: transtic('create.toast.create_error.title'),
          type: 'error',
          error: error,
        });
      },
      onSuccess: ({ id }) => {
        addToast({
          action: (
            <ToastButton>
              <Link to={`/berichtenbox/${id}`}>
                {transtic('create.toast.create_success.action')}
              </Link>
            </ToastButton>
          ),
          message: transtic('create.toast.create_success.message', { title }),
          title: transtic('create.toast.create_success.title'),
          type: 'success',
        });
      },
    },
  });

  const onSubmit = async (stepData: PartialNonConformitySchema) => {
    const mergedData = { ...data, ...stepData };
    setData(mergedData);

    if (step < stepComponents.length - 1) {
      setStep(step + 1);
    } else {
      const submitData = mergedData;
      const {
        files,
        placeName,
        measureField,
        rejections,
        observationDate,
        ...ticketData
      } = nonConformitySchema.parse(submitData);

      mutate({
        data: {
          ...ticketData,
          attachments: files
            ?.filter(({ file }) => file)
            .map(({ file }) => file),
          mainCategoryId: mainCategoryId!,
          nonConformityDetails: {
            measureField: measureField,
            observationDate: observationDate,
            placeName: placeName,
            rejections: rejections!,
          },
          subCategoryId: subCategoryId!,
          title,
        },
      });
    }
  };

  useEffect(() => {
    if (isSuccess) {
      navigate(createAnother ? '/berichtenbox/nieuw' : '/berichtenbox');
    }
  }, [createAnother, isSuccess, navigate]);

  return (
    <>
      <PageHead title={transtic('create.page_title')} />
      <NonConformityFormContext.Provider
        value={{
          data,
          setIsLoading,
          setStepShouldBlock: (stepShouldBlock) => {
            const toReturn = [...shouldBlock] satisfies typeof shouldBlock;
            toReturn[step] = stepShouldBlock;
            setShouldBlock(toReturn);
          },
        }}
      >
        <Step
          onBack={() => setStep(step - 1)}
          onSubmit={onSubmit}
          stepper={
            <div className="-mx-6 mt-4 flex justify-center border-t border-t-border-divider px-6 pt-3">
              <Stepper
                steps={[
                  {
                    children: transtic('create.steps.codes'),
                  },
                  {
                    children: transtic('create.steps.tickets'),
                  },
                  {
                    children: t('common.ticket_details'),
                  },
                ].map((s, index) => ({
                  ...s,
                  active: index === step,
                  done: index < step,
                  onClick: index < step ? () => setStep(index) : undefined,
                }))}
              />
            </div>
          }
          wizardActions={
            step === stepComponents.length - 1 ? (
              <>
                <Button
                  as={Link}
                  to={`/berichtenbox${params}`}
                  action="tertiary"
                  emphasis="subtle"
                >
                  {t('common.button.cancel')}
                </Button>
                <Stack direction="row" className="items-center" gap="lg">
                  <InputCheckbox
                    label={transtic('create.footer.create_another')}
                    name="createAnother"
                    onCheckedChange={(value) => {
                      setCreateAnother(value === true);
                    }}
                  />
                  <Button
                    type="submit"
                    isLoading={isPostLoading || isCategoryLoading || isLoading}
                  >
                    {t('common.submit')}
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                <Button
                  as={Link}
                  to={`/berichtenbox${params}`}
                  action="tertiary"
                  emphasis="subtle"
                >
                  {t('common.button.cancel')}
                </Button>
                <Button
                  type="submit"
                  action="secondary"
                  emphasis="bold"
                  isLoading={isPostLoading || isLoading}
                >
                  {t('common.steps.next')}
                </Button>
              </>
            )
          }
        />
        <CancelCreateTicketDialog
          shouldBlock={shouldBlock.some((step) => step) && !isSuccess}
        />
      </NonConformityFormContext.Provider>
    </>
  );
};
